// @flow
import React, { Fragment } from 'react';
import classnames from 'classnames';

import DynamicLink, {
  type Props as DynamicLinkProps,
} from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';

import { AdditionItemIcon } from './item-icon/AdditionItemIcon.jsx';
import { AdditionItemImage } from './item-image/AdditionItemImage.jsx';

import { themes } from './Addition.themes';

export type AdditionItemProps = {
  icon?: {
    alt?: string,
    name: IconName,
  },
  image?: {
    alt?: string,
    src: string,
  },
  label?: string,
  link?: DynamicLinkProps,
};

type AdditionProps = {
  className?: string,
  items: Array<AdditionItemProps>,
  ratioKey?: MediaRatios,
  theme?: string,
};

export function Addition(props: AdditionProps) {
  const { className, items = [], ratioKey, theme = 'default' } = props;

  const filteredItems = items.filter(({ icon = {}, image = {} }) => icon.name || image.src);

  if (!filteredItems.length) return null;

  const { thItem, thList, thRoot, thSymbol, thSymbolStyle } = themes[theme];

  const width = 100 / filteredItems.length;

  return (
    <div className={classnames(className, thRoot)}>
      <ul className={thList}>
        {filteredItems.map(({ icon = {}, image, label, link = {} }, idx) => {
          const isLink = !!link.url;

          const TagName = isLink ? DynamicLink : Fragment;
          const tagProps = isLink ? { ...link, className: 'block' } : null;

          const symbol =
            idx > 0 ? (
              <div className={thSymbol} style={thSymbolStyle}>
                {idx === filteredItems.length - 1 ? '=' : '+'}
              </div>
            ) : null;

          return (
            <li
              className={thItem}
              key={label}
              style={{ maxWidth: `${width}%`, minWidth: `${width}%` }}
            >
              <TagName {...tagProps}>
                {icon.name ? (
                  <AdditionItemIcon
                    alt={icon.alt}
                    iconName={icon.name}
                    label={label}
                    ratioKey={ratioKey}
                    symbol={symbol}
                    theme={theme}
                  />
                ) : (
                  <AdditionItemImage
                    image={image}
                    label={label}
                    ratioKey={ratioKey}
                    symbol={symbol}
                    theme={theme}
                  />
                )}
              </TagName>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
